import { Box, Button, Flex, Icon, IconButton, Stack, Text, Tooltip } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { uniqBy } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { BiX } from 'react-icons/bi';
import { MdSwapHoriz } from 'react-icons/md';
import { RiBook2Line } from 'react-icons/ri';

import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { Portfolio } from '../../../_openapi/portfolio-optimizer';
import { MIN_PAGE_SIZE } from '../../../constants';
import { PortfolioSelector } from '../../../widgets/PortfolioSelector';
import { makeFieldAccessors } from '../../../widgets/Table/columnHelpers/makeFieldAccessors';
import { Pagination } from '../../../widgets/Table/Pagination';
import { Table } from '../../../widgets/Table/Table';

const columnHelper = createColumnHelper<Portfolio>();
const base = makeFieldAccessors(columnHelper, (row) => row.base);
export const OptimizationPortfolioSelector = ({
    includedPortfolios,
    setCurrentPortfolios,
    setOtherPortfolios,
    listType,
    portfoliosData,
    onInvert,
}: {
    includedPortfolios: Portfolio[];
    setCurrentPortfolios: Dispatch<SetStateAction<Portfolio[]>>;
    setOtherPortfolios: Dispatch<SetStateAction<Portfolio[]>>;
    listType: 'inclusions' | 'exclusions';
    portfoliosData?: { portfolios: Portfolio[] };
    onInvert?: () => void;
}) => {
    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();

    const table = useReactTable({
        defaultColumn: {
            enableSorting: false,
            enableColumnFilter: false,
        },
        data: includedPortfolios,
        columns: [
            columnHelper.display({
                id: 'detailsBtn',
                cell: ({ row }) => (
                    <Flex gap={2}>
                        <IconButton
                            size="xs"
                            icon={<Icon as={BiX} />}
                            aria-label="move portfolio"
                            colorScheme="red"
                            onClick={() => {
                                const portfolio = row.original;
                                // Remove from current list
                                setCurrentPortfolios((current) =>
                                    current.filter((p) => p.base.portfolioGuid !== portfolio.base.portfolioGuid),
                                );
                                // Add to the other list
                                setOtherPortfolios((current) => [...current, portfolio]);
                            }}
                            w="20px"
                            minW="20px"
                            h="20px"
                            variant="outline"
                            borderRadius="sm"
                        />
                        <IconButton
                            size="xs"
                            icon={<Icon as={RiBook2Line} />}
                            aria-label="portfolio details"
                            onClick={() => viewPortfolio(row.original.base.pId)}
                            w="20px"
                            minW="20px"
                            h="20px"
                            variant="outline"
                            borderRadius="sm"
                        />
                    </Flex>
                ),
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                    isSticky: true,
                },
            }),
            base.text('portfolioGuid'),
            base.text('aC'),
            base.text('accountAlias'),
            base.money('nav'),
        ],
        initialState: {
            columnPinning: {
                left: ['detailsBtn'],
            },
            pagination: {
                pageIndex: 0,
                pageSize: MIN_PAGE_SIZE,
            },
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return (
        <Stack flex={1}>
            <Text fontWeight="medium">Portfolios</Text>
            <Flex gap={4} alignItems="center">
                <Box width="400px">
                    <PortfolioSelector
                        placeholder="Search and add portfolio to this list"
                        onSelect={(portfolio) => {
                            if (!portfolio.base) return;

                            // Remove from other list
                            setOtherPortfolios((current) =>
                                current.filter((p) => p.base.portfolioGuid !== portfolio.base.portfolioGuid),
                            );

                            // Add to current list (use uniqBy to prevent duplicates)
                            setCurrentPortfolios((current) => uniqBy([...current, portfolio], 'base.portfolioGuid'));
                        }}
                        isInvalid={false}
                        resetOnSelect
                    />
                </Box>

                <Button
                    size="sm"
                    colorScheme="teal"
                    disabled={!portfoliosData}
                    onClick={() => {
                        // Move all portfolios to current list, clear the other list
                        const allPortfolios = portfoliosData?.portfolios ?? [];
                        setCurrentPortfolios(allPortfolios);
                        setOtherPortfolios([]);
                    }}
                >
                    Add all
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        // Move all portfolios to other list, clear current list
                        const allPortfolios = portfoliosData?.portfolios ?? [];
                        setOtherPortfolios(allPortfolios);
                        setCurrentPortfolios([]);
                    }}
                >
                    Remove all
                </Button>
                {onInvert && (
                    <Tooltip label="Invert lists">
                        <IconButton
                            size="sm"
                            variant="outline"
                            icon={<Icon as={MdSwapHoriz} boxSize={5} />}
                            aria-label="Invert lists"
                            onClick={onInvert}
                        />
                    </Tooltip>
                )}
            </Flex>

            {includedPortfolios.length > 0 ? (
                <>
                    <Table table={table} />
                    {includedPortfolios.length > MIN_PAGE_SIZE && <Pagination table={table} />}
                </>
            ) : (
                <Text color="gray.500" fontStyle="italic" textAlign="center" py={4}>
                    All portfolios are {listType === 'inclusions' ? 'excluded' : 'included'}
                </Text>
            )}

            <PortfolioDrawer {...portfolioDrawerProps} />
        </Stack>
    );
};
